import React, { useCallback, useEffect, useMemo, useState } from "react";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import DailyLockedModal from "../components/DailyLockedModal";
import WordWheelApi from "../lib/api";
import { resolveWordWheelGridSize, WORD_WHEEL_DAILY_UNLOCK_LEVEL } from "../lib/constants";
import { useWordWheelAppearance } from "../context/WordWheelAppearanceContext";
import {
    isTabletLandscapeView,
    resolveWordWheelView,
    withWordWheelView,
} from "../lib/viewMode";
import {
    DAILY_PATH,
    MAIN_PATH,
    PLAY_PATH,
    RECORDING_1080P_PATH,
    SETTINGS_PATH,
    WORD_WHEEL_QUEST_TITLE,
} from "../routes";

function parseWords(wordsInUse) {
    if (!wordsInUse) return [];
    return wordsInUse.split("\n").map((w) => w.trim()).filter(Boolean);
}

function resolvePuzzleLevel(puzzle) {
    if (puzzle?.puzzleLevel != null && Number.isFinite(Number(puzzle.puzzleLevel))) {
        return Number(puzzle.puzzleLevel);
    }
    const fromDetails = puzzle?.details?.puzzleLevel;
    if (fromDetails != null && Number.isFinite(Number(fromDetails))) {
        return Number(fromDetails);
    }
    return null;
}

function MenuRow({ icon: Icon, label, subtitle, onPress, colors, locked }) {
    const TrailingIcon = locked ? LockOutlinedIcon : ChevronRightIcon;
    return (
        <Box
            component="button"
            type="button"
            onClick={onPress}
            sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                m: 0,
                mb: 1.25,
                p: 1.75,
                borderRadius: 2,
                border: `1px solid ${colors.border || colors.surfaceLight}`,
                bgcolor: colors.surface,
                cursor: "pointer",
                textAlign: "left",
                opacity: locked ? 0.78 : 1,
                boxShadow: colors.shadowSm,
                minHeight: 64,
            }}
        >
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.25,
                    bgcolor: colors.surfaceLight,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 1.5,
                    flexShrink: 0,
                }}
            >
                <Icon sx={{ fontSize: 20, color: locked ? colors.textMuted : colors.primaryGlow }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 16, color: colors.text }}>
                    {label}
                </Typography>
                {subtitle ? (
                    <Typography sx={{ fontSize: 13, mt: 0.25, lineHeight: 1.35, color: colors.textMuted }}>
                        {subtitle}
                    </Typography>
                ) : null}
            </Box>
            <TrailingIcon sx={{ color: colors.textMuted, fontSize: 20, flexShrink: 0 }} />
        </Box>
    );
}

export default function WordWheelMainPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const view = resolveWordWheelView(searchParams.get("view"));
    const tabletLandscape = isTabletLandscapeView(view);
    const { homeColors, isRandomScene } = useWordWheelAppearance();
    const colors = homeColors;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [puzzle, setPuzzle] = useState(null);
    const [dailyLockedVisible, setDailyLockedVisible] = useState(false);

    // `?view=tablet-landscape` is the 1920×1080 capture entry — not the home hub.
    // (Redirect is rendered after all hooks below.)

    const sceneText = isRandomScene
        ? {
            color: "#ffffff",
            textShadow: "0 1px 5px rgba(0,0,0,0.8)",
        }
        : null;
    const sceneMuted = isRandomScene
        ? {
            color: "rgba(255,255,255,0.88)",
            textShadow: "0 1px 4px rgba(0,0,0,0.75)",
        }
        : null;

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError("");
            try {
                const data = await WordWheelApi.fetchNext();
                if (data?.code === "NO_DATA") {
                    if (!cancelled) setError("No word wheel puzzle available yet.");
                    return;
                }
                if (!cancelled) setPuzzle(data);
            } catch (e) {
                if (!cancelled) setError(e?.message || "Failed to load puzzle");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const words = useMemo(() => parseWords(puzzle?.wordsInUse), [puzzle]);
    const gridSize = useMemo(() => resolveWordWheelGridSize(puzzle), [puzzle]);
    const puzzleLevel = useMemo(() => resolvePuzzleLevel(puzzle), [puzzle]);
    const journeyLevel = useMemo(() => {
        if (puzzle?.mainJourneyLevel != null && Number.isFinite(Number(puzzle.mainJourneyLevel))) {
            return Number(puzzle.mainJourneyLevel);
        }
        return puzzleLevel;
    }, [puzzle, puzzleLevel]);

    const dailyUnlocked =
        journeyLevel != null && journeyLevel >= WORD_WHEEL_DAILY_UNLOCK_LEVEL;
    const canPlay = Boolean(puzzle) && !loading && !error;

    const handlePlayJourney = useCallback(() => {
        if (puzzle?.id) navigate(withWordWheelView(PLAY_PATH, view));
    }, [navigate, puzzle, view]);

    const openDaily = useCallback(() => {
        if (dailyUnlocked) {
            navigate(withWordWheelView(DAILY_PATH, view));
            return;
        }
        setDailyLockedVisible(true);
    }, [dailyUnlocked, navigate, view]);

    const headerBlock = (
        <>
            <Typography
                variant="overline"
                sx={{
                    letterSpacing: 1.2,
                    fontWeight: 700,
                    display: "block",
                    mb: 1,
                    color: colors.textMuted,
                    ...sceneMuted,
                }}
            >
                Puzzle Collection
            </Typography>
            <Typography
                variant="h4"
                sx={{
                    fontWeight: 800,
                    letterSpacing: -0.4,
                    color: colors.text,
                    ...sceneText,
                }}
            >
                {WORD_WHEEL_QUEST_TITLE}
            </Typography>
            <Typography
                variant="body2"
                sx={{
                    mt: 0.75,
                    mb: 1,
                    lineHeight: 1.5,
                    color: colors.textMuted,
                    ...sceneMuted,
                }}
            >
                Swipe letters. Find every word.
                {tabletLandscape ? " · Tablet landscape" : ""}
            </Typography>
        </>
    );

    const journeyBlock = (
        <>
            <Typography
                variant="overline"
                sx={{
                    letterSpacing: 1.2,
                    fontWeight: 700,
                    display: "block",
                    mt: tabletLandscape ? 0 : 2.5,
                    mb: 1.25,
                    color: colors.textMuted,
                    ...sceneMuted,
                }}
            >
                Season Journey
            </Typography>

            <Box
                sx={{
                    borderRadius: 2,
                    border: `1px solid ${colors.border || colors.surfaceLight}`,
                    bgcolor: colors.surface,
                    p: 2.25,
                    boxShadow: colors.shadowMd,
                    mb: 1,
                    flex: tabletLandscape ? 1 : undefined,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {loading ? (
                    <CircularProgress size={28} sx={{ color: colors.primaryGlow, my: 3.5 }} />
                ) : error ? (
                    <Typography sx={{ color: "#dc2626", fontSize: 14, mb: 1.5 }}>{error}</Typography>
                ) : puzzle ? (
                    <>
                        {journeyLevel != null ? (
                            <Typography
                                sx={{
                                    fontSize: tabletLandscape ? 56 : 44,
                                    fontWeight: 800,
                                    letterSpacing: -0.8,
                                    lineHeight: 1.05,
                                    color: colors.text,
                                }}
                            >
                                Level {journeyLevel}
                            </Typography>
                        ) : null}
                        <Typography sx={{ mt: 1, fontSize: 15, fontWeight: 500, color: colors.textMuted }}>
                            {words.length > 0
                                ? `${words.length} words · ${gridSize}×${gridSize} grid`
                                : `${gridSize}×${gridSize} grid`}
                            {puzzle.season ? ` · ${String(puzzle.season).replace(/_/g, " ")}` : ""}
                        </Typography>
                    </>
                ) : null}

                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={!canPlay}
                    onClick={handlePlayJourney}
                    startIcon={<PlayArrowIcon />}
                    sx={{
                        mt: tabletLandscape ? "auto" : 2.5,
                        minHeight: 52,
                        borderRadius: 1.5,
                        bgcolor: colors.primary,
                        "&:hover": { bgcolor: colors.accentDark || colors.primaryGlow },
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "1.125rem",
                        boxShadow: colors.shadowSm,
                    }}
                >
                    Play
                </Button>
            </Box>
        </>
    );

    const moreBlock = (
        <>
            <Typography
                variant="overline"
                sx={{
                    letterSpacing: 1.2,
                    fontWeight: 700,
                    display: "block",
                    mt: tabletLandscape ? 0 : 2.5,
                    mb: 1.25,
                    color: colors.textMuted,
                    ...sceneMuted,
                }}
            >
                More
            </Typography>

            <MenuRow
                icon={CalendarMonthOutlinedIcon}
                label="Daily Puzzle"
                subtitle={
                    dailyUnlocked
                        ? "Today's bonus puzzle — separate from the season journey"
                        : `Unlocks at Season Journey level ${WORD_WHEEL_DAILY_UNLOCK_LEVEL}`
                }
                onPress={openDaily}
                colors={colors}
                locked={!dailyUnlocked}
            />

            <Typography
                sx={{
                    mt: 2,
                    fontSize: 12,
                    lineHeight: 1.5,
                    color: colors.textMuted,
                    ...sceneMuted,
                }}
            >
                Progress is saved on this device. Sign in once to move guest progress to your account.
            </Typography>
            <Typography
                sx={{
                    mt: 2,
                    mb: 1,
                    fontSize: 12,
                    fontWeight: 600,
                    textAlign: "center",
                    letterSpacing: 0.2,
                    color: colors.textMuted,
                    ...sceneMuted,
                }}
            >
                © Puzzle Interact
            </Typography>
        </>
    );

    if (tabletLandscape) {
        return <Navigate to={RECORDING_1080P_PATH} replace />;
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
                <IconButton
                    aria-label="Settings"
                    onClick={() =>
                        navigate(withWordWheelView(SETTINGS_PATH, view), {
                            state: { backPath: withWordWheelView(MAIN_PATH, view) },
                        })
                    }
                    sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 1.5,
                        bgcolor: colors.surface,
                        color: colors.text,
                        boxShadow: colors.shadowSm,
                        "&:hover": { bgcolor: colors.surfaceLight },
                    }}
                >
                    <SettingsOutlinedIcon />
                </IconButton>
            </Box>

            {tabletLandscape ? (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "1.1fr 0.9fr",
                        gap: 3,
                        flex: 1,
                        minHeight: 0,
                        alignItems: "stretch",
                    }}
                >
                    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
                        {headerBlock}
                        {journeyBlock}
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, pt: 5 }}>
                        {moreBlock}
                    </Box>
                </Box>
            ) : (
                <>
                    {headerBlock}
                    {journeyBlock}
                    {moreBlock}
                </>
            )}

            <DailyLockedModal
                open={dailyLockedVisible}
                onClose={() => setDailyLockedVisible(false)}
                colors={colors}
            />
        </Box>
    );
}
